import { type Dayjs } from 'dayjs';
import { BY_TARGET_SERIES_TYPE } from '@/common/define/widget.define';
import { CHART_PASSING_VALUE } from '@/common/utils';
import type { MetricChartInfo as CommonMetricChartInfo, SeriesData } from '@/common/utils/types';
import type { MetricResponse } from '@/api-client/data/model';
import type { MetricChartOption, MetricResult, TransformMetricOptions } from './metric.types';
import { fillNullByInterval } from './fillNull.utils';

type ChartDataDataType = Record<string, (string | number | null)[]>;
type ChartDataSeriesType = Record<string, SeriesData>;

const convertMetricResponseToChartData = (
  response: MetricResponse,
  responseIndex: number,
  option: TransformMetricOptions,
): { data: ChartDataDataType; series: ChartDataSeriesType; labels: (number | Dayjs)[] } => {
  const {
    aggregationType,
    summaryType,
    dataDefinition,
    targetIds,
    targetNames,
    targetAliases,
    metrics,
    interval,
    fromTime,
    toTime,
  } = response;
  const responseId = `${dataDefinition?.dataId}_${responseIndex}`;

  const { seriesIdFormat, seriesDataFormat, seriesNameFormat, dataFormat, targetSortCallback } =
    option;
  const data: ChartDataDataType = {};
  const series: ChartDataSeriesType = {};
  let labels: (number | Dayjs)[] = [];

  const sortedTargetIds = targetIds ? [...targetIds].sort(targetSortCallback) : [];
  const sortedTargetNames =
    targetNames && targetIds
      ? sortedTargetIds.map((targetId) => targetNames[targetIds.indexOf(targetId)] ?? '')
      : [];
  const sortedTargetAliases =
    targetAliases && targetIds
      ? sortedTargetIds.map((targetId) => targetAliases[targetIds.indexOf(targetId)] ?? '')
      : [];

  sortedTargetIds.forEach((targetId, targetIdx) => {
    const isByTarget = aggregationType === BY_TARGET_SERIES_TYPE;
    const metricIndex =
      (isByTarget
        ? metrics?.findIndex(({ targetId: metricTargetId }) => metricTargetId === targetId)
        : 0) || 0;
    const byTargetIndex = isByTarget ? targetIdx : 0;
    const metricData = metrics?.[metricIndex];

    const seriesId =
      seriesIdFormat?.({
        targetId,
        targetIndex: byTargetIndex,
        summaryType: summaryType ?? '',
        dataId: dataDefinition?.dataId,
      }) || `${responseId}${isByTarget ? `_${targetIdx}` : ''}`;

    const targetName = metricData?.targetName || sortedTargetNames[targetIdx] || '';
    const seriesName =
      seriesNameFormat?.({
        targetName,
        targetAlias: sortedTargetAliases[targetIdx] || targetName,
        targetIndex: byTargetIndex,
        responseIndex,
        response,
      }) ||
      dataDefinition?.dataId ||
      '';

    const totalIndex = sortedTargetIds.length * responseIndex + targetIdx;
    const seriesOption =
      seriesDataFormat?.({
        seriesId,
        metricIndex: byTargetIndex,
        responseIndex,
        response,
        index: totalIndex,
      }) || {};

    series[seriesId] = {
      ...seriesOption,
      id: dataDefinition?.dataId,
      name: seriesOption?.name || seriesName, // seriesOption 쪽 seriesName 의 우선순위가 더 높음
      passingValue: CHART_PASSING_VALUE,
    };

    const nullFilledMetricData = fillNullByInterval(metricData?.values ?? [], {
      interval,
      fromTime,
      toTime,
      useUtcZeroTime: false,
      valueFormatter: dataFormat,
    });
    labels = nullFilledMetricData.label;
    data[seriesId] = nullFilledMetricData.value;
  });
  return { data, series, labels };
};

// 일반 ChartData 형식으로 사용하려면 반환값의 chartData 만 매핑해서 사용
export const transformMetricResponseToMetricChartInfo = (
  data: MetricResponse[],
  options: TransformMetricOptions = {},
): MetricResult => {
  if (!data?.length) {
    return { data: [], error: null };
  }

  const metricChartInfo = data.map<CommonMetricChartInfo>((response, index) => {
    const { dataDefinition } = response;
    const { unit = '', displayName = '', dataId = '' } = dataDefinition ?? {};

    const {
      data: chartData,
      series,
      labels,
    } = convertMetricResponseToChartData(response, index, options);

    return {
      chartData: { data: chartData, series, labels, groups: [] },
      title: displayName,
      chartId: dataId,
      unit,
    };
  });

  return {
    data: metricChartInfo,
    error: data.find(({ status, reason }) => status !== 'success' && !!reason)?.reason ?? null,
  };
};

// NOTE: null 값 채워주는 로직
export const transformData = ({
  data,
  transformOption,
  chartOption,
}: {
  data: MetricResponse[];
  transformOption?: TransformMetricOptions;
  chartOption?: Partial<MetricChartOption>;
}) => {
  const { seriesIdFormat, seriesDataFormat, seriesNameFormat, dataFormat } = transformOption || {};
  const { displayMode = 'line', linePoint = false, barThickness = 1, fill } = chartOption ?? {};

  return transformMetricResponseToMetricChartInfo(data || [], {
    seriesIdFormat,
    seriesDataFormat: (params) => {
      const formattedSeries = seriesDataFormat?.(params) || {};

      return {
        ...formattedSeries,
        type: displayMode ?? formattedSeries.type ?? 'line',
        point: linePoint ?? formattedSeries.point ?? false,
        thickness: barThickness ?? formattedSeries.thickness ?? 1,
        fill: fill ?? formattedSeries.fill ?? false,
      };
    },
    seriesNameFormat,
    dataFormat,
  });
};
